import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import {
    AirOutlined,
    DeviceThermostatOutlined,
    FavoriteBorderOutlined,
    FavoriteOutlined,
    MonitorWeightOutlined,
    WaterDropOutlined,
    ScienceOutlined
} from '@mui/icons-material';
import { Box, Button, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { getMeasurements } from '../../../../services/measurements';
import { useUser } from '../../../../hooks/useUser';
import { clinicalMeasurementTypes } from '../../../../configs/clinicalMeasurements';

const PERIOD_DAYS = { '7d': 7, '30d': 30, '90d': 90 };

const MEASUREMENT_TYPES = [
    {
        type: 'M_HEARTRATE',
        labelKey: 'userPage.metrics.heartRate',
        icon: FavoriteBorderOutlined,
        unit: 'bpm',
        lines: [
            {
                key: 'heartRate',
                sourceKeys: ['hr'],
                labelKey: 'userPage.metrics.heartRate',
                color: '#0A6FB4'
            }
        ]
    },
    {
        type: 'M_BLOOD_PRESSURE',
        labelKey: 'userPage.metrics.bloodPressure',
        icon: FavoriteOutlined,
        unit: 'mmHg',
        lines: [
            {
                key: 'systolic',
                sourceKeys: ['systolic'],
                labelKey: 'userPage.measurements.columns.systolic',
                color: '#0A6FB4'
            },
            {
                key: 'diastolic',
                sourceKeys: ['diastolic'],
                labelKey: 'userPage.measurements.columns.diastolic',
                color: '#0A6FB4',
                strokeDasharray: '6 4'
            }
        ]
    },
    {
        type: 'M_BLOOD_OXYGEN',
        labelKey: 'userPage.metrics.bloodOxygen',
        icon: AirOutlined,
        unit: '%',
        lines: [
            {
                key: 'spo2',
                sourceKeys: ['spo2'],
                labelKey: 'userPage.metrics.bloodOxygen',
                color: '#0A6FB4'
            }
        ]
    },
    {
        type: 'M_BLOOD_SUGAR',
        labelKey: 'userPage.metrics.bloodSugar',
        icon: WaterDropOutlined,
        unit: 'mg/dL',
        lines: [
            {
                key: 'bloodSugar',
                sourceKeys: ['glicose', 'glucose'],
                labelKey: 'userPage.metrics.bloodSugar',
                color: '#0A6FB4'
            }
        ]
    },
    {
        type: 'M_BODY_TEMPERATURE',
        labelKey: 'userPage.metrics.temperature',
        icon: DeviceThermostatOutlined,
        unit: '°C',
        lines: [
            {
                key: 'temperature',
                sourceKeys: ['temperature'],
                labelKey: 'userPage.metrics.temperature',
                color: '#0A6FB4'
            }
        ]
    },
    {
        type: 'M_WEIGHT',
        labelKey: 'userPage.metrics.weight',
        icon: MonitorWeightOutlined,
        unit: 'kg',
        lines: [
            {
                key: 'weight',
                sourceKeys: ['weight'],
                labelKey: 'userPage.metrics.weight',
                color: '#0A6FB4'
            }
        ]
    }
];

const CLINICAL_MEASUREMENT_TYPES = [
    ...MEASUREMENT_TYPES,
    ...clinicalMeasurementTypes
        .filter((measurement) => !measurement.overviewAsSingleMetric)
        .map((measurement) => ({
            type: measurement.apiType,
            labelKey: measurement.labelKey,
            icon: ScienceOutlined,
            unit: measurement.fields[0].unit,
            lines: measurement.fields.map((field, index) => ({
                key: field.field,
                sourceKeys: [field.field],
                label: field.label,
                labelKey: field.labelKey,
                color: ['#0A6FB4', '#289C96', '#B05A8C', '#C58A24'][index]
            }))
        }))
];

const getPeriodStart = (period, patientCreated, plans) => {
    if (PERIOD_DAYS[period]) {
        return dayjs()
            .subtract(PERIOD_DAYS[period] - 1, 'day')
            .startOf('day');
    }

    const today = dayjs();
    const createdDate = dayjs(patientCreated);
    if (patientCreated && createdDate.isValid() && !createdDate.isAfter(today, 'day')) {
        return createdDate.startOf('day');
    }

    const earliestPlanStart = plans
        .filter(
            (plan) =>
                plan.accepted &&
                dayjs(plan.start_date).isValid() &&
                !dayjs(plan.start_date).isAfter(today, 'day')
        )
        .map((plan) => dayjs(plan.start_date).startOf('day'))
        .sort((first, second) => first.valueOf() - second.valueOf())[0];

    return earliestPlanStart || dayjs().subtract(1, 'year').startOf('day');
};

const getArrayValue = (data, sourceKeys, index) => {
    const sourceKey = sourceKeys.find((key) => Array.isArray(data?.[key]));
    const value = sourceKey ? data[sourceKey][index] : null;

    return value !== null &&
        value !== undefined &&
        value !== '' &&
        Number.isFinite(Number(value))
        ? Number(value)
        : null;
};

const normalizeMeasurements = (data, measurement) => {
    const timestamps = Array.isArray(data?.ts) ? data.ts : [];

    return timestamps
        .map((timestamp, index) => {
            const point = { timestamp };
            measurement.lines.forEach((line) => {
                point[line.key] = getArrayValue(data, line.sourceKeys, index);
            });
            return point;
        })
        .filter(
            (point) =>
                dayjs(point.timestamp).isValid() &&
                measurement.lines.some((line) => point[line.key] !== null)
        );
};

const MeasurementsEvolutionChart = ({
    patientUuid,
    patientCreated,
    plans,
    plansLoading,
    plansError,
    period,
    source = 'patient'
}) => {
    const { t, i18n } = useTranslation();
    const { user } = useUser();
    const zone = user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const [selectedTypes, setSelectedTypes] = useState({
        patient: MEASUREMENT_TYPES[0].type,
        clinician: MEASUREMENT_TYPES[0].type
    });
    const availableTypes =
        source === 'patient' ? MEASUREMENT_TYPES : CLINICAL_MEASUREMENT_TYPES;
    const selectedType = selectedTypes[source];
    const selectedMeasurement = availableTypes.find(
        (measurement) => measurement.type === selectedType
    );
    const startDateKey = getPeriodStart(period, patientCreated, plans).format(
        'YYYY-MM-DD'
    );
    const endDateKey = dayjs().format('YYYY-MM-DD');
    const locale = i18n.resolvedLanguage?.startsWith('pt') ? 'pt-PT' : 'en-GB';
    const axisDateFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                day: period === 'all' ? undefined : 'numeric',
                month: 'short',
                year: period === 'all' ? '2-digit' : undefined
            }),
        [locale, period]
    );
    const tooltipDateFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }),
        [locale]
    );

    const measurementsQuery = useQuery({
        queryKey: [
            'patient-measurements',
            patientUuid,
            selectedType,
            startDateKey,
            endDateKey,
            zone,
            source
        ],
        queryFn: ({ signal }) =>
            getMeasurements(patientUuid, selectedType, startDateKey, endDateKey, {
                timezone: zone,
                source: source === 'patient' ? 'PATIENT' : 'DOCTOR',
                signal
            }),
        enabled: Boolean(patientUuid) && !plansLoading && !plansError,
        staleTime: 60 * 1000
    });
    const chartData = useMemo(
        () => normalizeMeasurements(measurementsQuery.data, selectedMeasurement),
        [measurementsQuery.data, selectedMeasurement]
    );
    const isLoading = plansLoading || (!plansError && measurementsQuery.isPending);
    const isError = plansError || measurementsQuery.isError;

    return (
        <Box sx={{ display: 'flex', height: 300, flexDirection: 'column' }}>
            <Tabs
                value={selectedType}
                onChange={(_event, nextType) =>
                    setSelectedTypes((current) => ({ ...current, [source]: nextType }))
                }
                variant={source === 'patient' ? 'fullWidth' : 'scrollable'}
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label={t('userPage.measurements.metricSelector')}
                sx={{
                    minHeight: 34,
                    mb: 0.75,
                    borderBottom: '1px solid #E0E5E9',
                    '& .MuiTabs-indicator': {
                        height: 3,
                        borderRadius: '3px 3px 0 0',
                        transition:
                            'left 220ms cubic-bezier(0.4, 0, 0.2, 1), width 220ms cubic-bezier(0.4, 0, 0.2, 1)'
                    },
                    '& .MuiTab-root': {
                        minWidth: 0,
                        minHeight: 34,
                        gap: 0.5,
                        px: 0.75,
                        py: 0.5,
                        color: '#52616B',
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        letterSpacing: 0,
                        textTransform: 'none',
                        '&.Mui-selected': { color: '#0A579B' },
                        '& .MuiTab-iconWrapper': {
                            width: 17,
                            height: 17,
                            mb: '0 !important'
                        }
                    }
                }}
            >
                {availableTypes.map((measurement) => {
                    const Icon = measurement.icon;
                    return (
                        <Tab
                            key={measurement.type}
                            value={measurement.type}
                            icon={<Icon fontSize="small" />}
                            iconPosition="start"
                            label={t(measurement.labelKey)}
                        />
                    );
                })}
            </Tabs>

            {isLoading ? (
                <Box sx={{ display: 'grid', flex: 1, gap: 1, pt: 1 }}>
                    <Skeleton width="28%" height={20} />
                    <Skeleton variant="rounded" height={225} />
                </Box>
            ) : isError ? (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5
                    }}
                >
                    <Typography color="error" variant="body2">
                        {t(
                            'userPage.overview.rehabilitation.measurementsChart.loadError'
                        )}
                    </Typography>
                    {!plansError && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => measurementsQuery.refetch()}
                        >
                            {t('userPage.overview.measurements.retry')}
                        </Button>
                    )}
                </Box>
            ) : chartData.length === 0 ? (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {t('userPage.overview.rehabilitation.measurementsChart.noData')}
                    </Typography>
                </Box>
            ) : (
                <Box
                    role="img"
                    aria-label={t(
                        'userPage.overview.rehabilitation.measurementsChart.chartLabel',
                        { metric: t(selectedMeasurement.labelKey) }
                    )}
                    sx={{ flex: 1, minHeight: 0 }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={chartData}
                            margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid
                                vertical={false}
                                stroke="#E6EDF2"
                                strokeDasharray="3 3"
                            />
                            <XAxis
                                dataKey="timestamp"
                                axisLine={{ stroke: '#CAD5DD' }}
                                tickLine={false}
                                minTickGap={28}
                                tick={{ fill: '#74838E', fontSize: 10 }}
                                tickFormatter={(value) =>
                                    axisDateFormatter.format(dayjs(value).toDate())
                                }
                            />
                            <YAxis
                                width={48}
                                domain={['auto', 'auto']}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: '#74838E', fontSize: 10 }}
                                label={{
                                    value: selectedMeasurement.unit,
                                    angle: -90,
                                    position: 'insideLeft',
                                    fill: '#74838E',
                                    fontSize: 10
                                }}
                            />
                            <Tooltip
                                labelFormatter={(value) =>
                                    tooltipDateFormatter.format(dayjs(value).toDate())
                                }
                                formatter={(value, name) => [
                                    `${value} ${selectedMeasurement.unit}`,
                                    name
                                ]}
                                contentStyle={{
                                    border: '1px solid #D7E0E6',
                                    borderRadius: 6,
                                    boxShadow: '0 4px 12px rgba(38, 50, 56, 0.12)',
                                    fontSize: 11
                                }}
                            />
                            <Legend
                                verticalAlign="top"
                                align="right"
                                height={24}
                                iconType="plainline"
                                iconSize={14}
                                wrapperStyle={{ color: '#5E6C76', fontSize: 10 }}
                            />
                            {selectedMeasurement.lines.map((line) => (
                                <Line
                                    key={line.key}
                                    type="monotone"
                                    dataKey={line.key}
                                    name={line.label || t(line.labelKey)}
                                    stroke={line.color}
                                    strokeWidth={2}
                                    strokeDasharray={line.strokeDasharray}
                                    dot={{ r: 2.5, fill: line.color, strokeWidth: 0 }}
                                    activeDot={{ r: 4, strokeWidth: 0 }}
                                    connectNulls={false}
                                    animationDuration={450}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </Box>
    );
};

MeasurementsEvolutionChart.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    patientCreated: PropTypes.string,
    plans: PropTypes.arrayOf(PropTypes.object).isRequired,
    plansLoading: PropTypes.bool.isRequired,
    plansError: PropTypes.bool.isRequired,
    period: PropTypes.oneOf(['7d', '30d', '90d', 'all']).isRequired,
    source: PropTypes.oneOf(['patient', 'clinician'])
};

export default MeasurementsEvolutionChart;
